import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  TableFooter,
} from "@material-ui/core";
import { map, not } from "ramda";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import Typography from "@material-ui/core/Typography";
import TablePagination from "@material-ui/core/TablePagination";
import { Link, useRouteMatch } from "react-router-dom";
import { useParsedMetadataForJob } from "./processingDiscoveryServices";
import { DateTime } from "luxon";
import Tooltip from "@material-ui/core/Tooltip";
import OpenIcon from "@material-ui/icons/Launch";
import IconButton from "@material-ui/core/IconButton";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },

  link: {
    color: theme.palette.primary.brandPrimary,
  },

  cell: {
    minWidth: 200,
    maxWidth: 600,
  },

  countCell: {
    minWidth: 50,
    maxWidth: 60,
  },
}));

const MetadataFileDiscoveryMediaCard = ({ jobId }) => {
  const classes = useStyles();
  const { url } = useRouteMatch();

  const [parsedMedia, setParsedMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const parsedMetadataResponse = useParsedMetadataForJob(jobId, page);

  useEffect(() => {
    if (
      parsedMetadataResponse.isSuccess &&
      notBlank(parsedMetadataResponse.data)
    ) {
      setParsedMedia(parsedMetadataResponse.data.content);
      setTotal(parsedMetadataResponse.data.totalElements);
    } else {
      setParsedMedia([]);
      setTotal(0);
    }
  }, [parsedMetadataResponse.isSuccess, parsedMetadataResponse.data]);

  const handlePageChange = useCallback(page => setPage(page), []);

  const [collapsed, setCollapsed] = useState(false);
  const handleCollapseClick = useCallback(() => {
    setCollapsed(prev => !prev);
  }, []);

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Parsed Metadata"
        classes={{ root: classes.header }}
        action={
          <Tooltip title="Add Application">
            <IconButton onClick={handleCollapseClick}>
              {collapsed ? (
                <ExpandMoreIcon color="primary" fontSize="large" />
              ) : (
                <ExpandLessIcon color="primary" fontSize="large" />
              )}
            </IconButton>
          </Tooltip>
        }
      />
      {not(collapsed) && (
        <CardContent classes={{ root: classes.content }}>
          {parsedMetadataResponse.isError && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              <Typography color="error">
                Error loading Metadata File Discovery Errors.
              </Typography>
            </Box>
          )}
          {parsedMetadataResponse.isLoading && <PanelLoadingIndicator />}
          {parsedMetadataResponse.isSuccess && isBlank(parsedMedia) && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              No Errors with the Metadata File Discovery Event.
            </Box>
          )}
          {parsedMetadataResponse.isSuccess && notBlank(parsedMedia) && (
            <TableContainer>
              <Table stickyHeader aria-label="Metadata File Discovery Errors">
                <TableHead>
                  <TableRow>
                    <TableCell
                      key="eventTimestamp"
                      align="left"
                      className={classes.contentCell}
                    >
                      Timestamp
                    </TableCell>

                    <TableCell
                      key="mediaId"
                      align="left"
                      className={classes.cell}
                    >
                      Media Id
                    </TableCell>

                    <TableCell key="isbn" align="left" className={classes.cell}>
                      ISBN
                    </TableCell>

                    <TableCell
                      key="title"
                      align="left"
                      className={classes.cell}
                    >
                      Title
                    </TableCell>

                    <TableCell
                      key="errorCount"
                      align="center"
                      className={classes.countCell}
                    >
                      Error Count
                    </TableCell>

                    <TableCell
                      key="actions"
                      align="center"
                      style={{ width: 100 }}
                    />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {map(
                    media => (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={
                          media.mediaId +
                          DateTime.fromISO(media.eventTimestamp).toMillis()
                        }
                      >
                        <TableCell
                          key={
                            "eventTimestamp:" +
                            media.mediaId +
                            DateTime.fromISO(media.eventTimestamp).toMillis()
                          }
                          align="left"
                          className={classes.contentCell}
                        >
                          {DateTime.fromISO(media.eventTimestamp)
                            .toUTC()
                            .toLocaleString(
                              DateTime.DATETIME_SHORT_WITH_SECONDS,
                            )}
                        </TableCell>

                        <TableCell
                          key={
                            "mediaId:" +
                            media.mediaId +
                            DateTime.fromISO(media.eventTimestamp).toMillis()
                          }
                          align="left"
                          className={classes.contentCell}
                        >
                          <Tooltip title="View Event">
                            <Link
                              className={classes.link}
                              to={`${url}/${media.mediaId}`}
                            >
                              {media.mediaId}
                            </Link>
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          key={
                            "isbn:" +
                            media.mediaId +
                            DateTime.fromISO(media.eventTimestamp).toMillis()
                          }
                          align="left"
                        >
                          {media.isbn}
                        </TableCell>

                        <TableCell
                          key={
                            "title:" +
                            media.mediaId +
                            DateTime.fromISO(media.eventTimestamp).toMillis()
                          }
                          align="left"
                        >
                          {media.title}
                        </TableCell>

                        <TableCell
                          key={
                            "errorCount:" +
                            media.mediaId +
                            DateTime.fromISO(media.eventTimestamp).toMillis()
                          }
                          align="center"
                        >
                          {media.errorCount}
                        </TableCell>

                        <TableCell
                          key={
                            "actions:" +
                            media.mediaId +
                            DateTime.fromISO(media.eventTimestamp).toMillis()
                          }
                          align="center"
                          style={{ width: 100 }}
                        >
                          {media.errorCount === 0 && (
                            <Tooltip title="View Content">
                              <Link
                                className={classes.link}
                                to={{
                                  pathname: `/creator/content/${media.mediaId}`,
                                  state: { from: url, creatorRelative: false },
                                }}
                              >
                                <OpenIcon fontSize="large" color="primary" />
                              </Link>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ),
                    parsedMedia,
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      classes={{
                        root: classes.pagination,
                      }}
                      rowsPerPageOptions={[10]}
                      colSpan={6}
                      count={total}
                      rowsPerPage={10}
                      page={page}
                      onChangePage={(event, page) => handlePageChange(page)}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default MetadataFileDiscoveryMediaCard;
